import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Properties, Property } from '../ui/shared/properties'
import { getImage } from '../../exif-utils'
import { areaToString } from '../../units'
import area from '@turf/area'
import Button from '../ui/elements/button'
import BandsModal, { useBandsConfiguration } from './bands-modal'
import { getDefaultDisplayBands } from '../../ortho-utils'

export default function GeoTIFFMetadata({
  file,
  imageFeature,
  units,
  setImageProperties,
}) {
  const [imgsrc, setImgsrc] = useState(null)

  useEffect(() => {
    setImgsrc(null)
    getPreviewImage()
      .then(img => {
        if (img) {
          setImgsrc(img.src)
        }
      })
      .catch(err => console.debug('Failed to get preview image', err))

    function getPreviewImage() {
      try {
        if (imageFeature) {
          const bands = imageFeature.properties.bands
          return getImage(imageFeature, () => file, {
            maxDimensions: { width: 1024, height: 1024 },
            bands: getDefaultDisplayBands(bands),
          })
        }
      } catch (err) {
        console.warn('Failed to get preview image', err)
      }
      return Promise.resolve()
    }
  }, [file, imageFeature])

  const properties = imageFeature && imageFeature.properties // check length
  const bbox = imageFeature && imageFeature.bbox // check length
  const [bandsValid, bandConfigurationName] = useBandsConfiguration(
    properties?.bands,
    properties?.samplesPerPixel,
    true
  )
  const [showBandModal, setShowBandModal] = useState(false)

  return (
    (properties && (
      <>
        <div>
          <img src={imgsrc} className="tw-w-full tw-bg-gray-200" />
          <div className="tw-mt-5">
            <p className="tw-m-0 tw-uppercase tw-mb-2 tw-truncate">
              {properties.filename}
            </p>
            <Properties>
              <Property
                title="Latitude"
                value={`${formatDegrees(bbox[1], 'NS')} - ${formatDegrees(
                  bbox[3],
                  'NS'
                )}`}
              />
              <Property
                title="Longitude"
                value={`${formatDegrees(bbox[0], 'EW')} - ${formatDegrees(
                  bbox[2],
                  'EW'
                )}`}
              />
              <Property
                title="Area"
                value={areaToString(area(imageFeature), units, {})}
              />
              <Property title="Width" value={`${properties.width} pixels`} />
              <Property title="Height" value={`${properties.height} pixels`} />
              <Property
                title="Projection"
                value={`${
                  properties.ProjectedCSTypeGeoKey
                    ? `EPSG:${properties.ProjectedCSTypeGeoKey}`
                    : properties.GeogCitationGeoKey ||
                      `EPSG:${properties.GeographicTypeGeoKey}`
                }`}
              />
              <Property
                title="Bands"
                value={
                  <Button onClick={() => setShowBandModal(true)}>
                    {bandsValid ? bandConfigurationName : 'Not Selected'}
                  </Button>
                }
              />
            </Properties>
          </div>
        </div>
        <BandsModal
          numberBands={properties.samplesPerPixel}
          bands={properties.bands}
          show={showBandModal}
          onSave={bands => {
            setImageProperties({ ...properties, bands })
            setShowBandModal(false)
          }}
          onDismiss={() => setShowBandModal(false)}
        />
      </>
    )) || (
      <div className="tw-uppercase">
        <p>No preview available</p>
      </div>
    )
  )

  function formatDegrees(x, prefixes) {
    return `${x >= 0 ? prefixes[0] : prefixes[1]} ${Math.abs(x).toFixed(4)}°`
  }
}

GeoTIFFMetadata.propTypes = {
  imageFeature: PropTypes.object,
  file: PropTypes.object,
  units: PropTypes.string,
  setImageProperties: PropTypes.func.isRequired,
}
